import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type StockArticle, type StockArticleFieldChanges } from "@/lib/stock.api";
import { useUpdateStockArticleMutation } from "@/hooks/mutations/useUpdateStockArticle";
import { useRequestStockEditMutation } from "@/hooks/mutations/useRequestStockEdit";

const editArticleSchema = z.object({
  name: z.string().min(1, "Le nom est requis."),
  unit: z.string().optional(),
  sellingPrice: z
    .number({ invalid_type_error: "Requis." })
    .positive("Le prix de vente doit être supérieur à 0."),
  supplierPrice: z.number().min(0).optional(),
  minLevel: z.number().min(0).optional(),
});

type EditArticleFormValues = z.infer<typeof editArticleSchema>;

function toValues(article: StockArticle): EditArticleFormValues {
  return {
    name: article.name,
    unit: article.unit,
    sellingPrice: parseFloat(article.defaultSellingPrice),
    supplierPrice: parseFloat(article.defaultSupplierPrice),
    minLevel: article.minLevel,
  };
}

// Only the fields that actually differ from the current article — an
// unchanged "change" is just noise for the reviewer (request mode) and a
// pointless write (direct mode).
function diffChanges(article: StockArticle, values: EditArticleFormValues): StockArticleFieldChanges {
  const changes: StockArticleFieldChanges = {};
  if (values.name !== article.name) changes.name = values.name;
  if ((values.unit ?? "") !== article.unit) changes.unit = values.unit ?? "";
  if (values.sellingPrice !== parseFloat(article.defaultSellingPrice))
    changes.defaultSellingPrice = values.sellingPrice;
  if ((values.supplierPrice ?? 0) !== parseFloat(article.defaultSupplierPrice))
    changes.defaultSupplierPrice = values.supplierPrice ?? 0;
  if ((values.minLevel ?? 0) !== article.minLevel) changes.minLevel = values.minLevel ?? 0;
  return changes;
}

interface EditArticleDialogProps {
  article: StockArticle | null;
  // true for admin/super_admin (direct edit); false for manager (edit request)
  canEditDirect: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

export function EditArticleDialog({
  article,
  canEditDirect,
  onClose,
  onUpdated,
}: EditArticleDialogProps) {
  const updateMutation = useUpdateStockArticleMutation();
  const requestMutation = useRequestStockEditMutation();
  const [reason, setReason] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<EditArticleFormValues>({
    resolver: zodResolver(editArticleSchema),
    defaultValues: { name: "", unit: "unit", sellingPrice: 0, supplierPrice: 0, minLevel: 0 },
  });

  useEffect(() => {
    if (article) {
      reset(toValues(article));
      setReason("");
    }
  }, [article, reset]);

  async function onSubmit(values: EditArticleFormValues) {
    if (!article) return;
    const changes = diffChanges(article, values);
    if (Object.keys(changes).length === 0) return;

    try {
      if (canEditDirect) {
        await updateMutation.mutateAsync({
          id: article.id,
          data: {
            name: values.name,
            unit: values.unit,
            defaultSellingPrice: values.sellingPrice,
            defaultSupplierPrice: values.supplierPrice,
            minLevel: values.minLevel,
          },
        });
      } else {
        await requestMutation.mutateAsync({
          articleId: article.id,
          targetType: "article_fields",
          reason,
          proposedChanges: changes,
        });
      }
      onUpdated();
      onClose();
    } catch {
      // Error toast already handled by the global mutation default.
    }
  }

  const pending = isSubmitting || updateMutation.isPending || requestMutation.isPending;

  return (
    <Dialog
      open={!!article}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {canEditDirect ? "Modifier l'article" : "Demander une modification de l'article"}
          </DialogTitle>
        </DialogHeader>

        {article && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {!canEditDirect && (
              <p className="text-[11px] text-muted-foreground">
                Cette demande sera soumise à validation — l'article ne changera qu'après
                approbation par un administrateur.
              </p>
            )}
            <div>
              <label className="block text-[11.5px] font-medium text-body mb-1.5">Nom</label>
              <Input {...register("name")} autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11.5px] font-medium text-body mb-1.5">Unité</label>
                <Input {...register("unit")} placeholder="unit, boîte, ramette..." />
              </div>
              <div>
                <label className="block text-[11.5px] font-medium text-body mb-1.5">
                  Seuil bas
                </label>
                <Input type="number" {...register("minLevel", { valueAsNumber: true })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11.5px] font-medium text-body mb-1.5">
                  Prix de vente par défaut
                </label>
                <Input type="number" {...register("sellingPrice", { valueAsNumber: true })} />
              </div>
              <div>
                <label className="block text-[11.5px] font-medium text-body mb-1.5">
                  Prix fournisseur par défaut
                </label>
                <Input type="number" {...register("supplierPrice", { valueAsNumber: true })} />
              </div>
            </div>

            {!canEditDirect && (
              <div>
                <label className="block text-[11.5px] font-medium text-body mb-1.5">
                  Raison de la modification (obligatoire)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="Pourquoi ce changement est-il nécessaire ?"
                  className="flex w-full rounded border border-border bg-card px-3 py-2 text-sm resize-none"
                />
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" disabled={pending || (!canEditDirect && !reason.trim())}>
                {pending ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : canEditDirect ? (
                  "Enregistrer"
                ) : (
                  "Soumettre la demande"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
